import React, { useRef, useState } from "react";
import Avatar from "antd/es/avatar/Avatar";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { assignMasterBeacon } from "../../../core/data/redux/masterBeaconSlice";
import { useDispatch, useSelector } from "react-redux";
import Toasts from "../../../core/common/toast";
import { getEmployees } from "../../../core/data/redux/employeesSlice";

interface AssignMasterBeaconProps {
  beacon: any;
}

const AssignMasterBeacon = (props: AssignMasterBeaconProps) => {
  const { beacon } = props;
  const tableRef = useRef<{ resetSelection: ([]) => void }>(null);
  const dispatch = useDispatch();
  const masterBeaconState = useSelector((state: any) => state.masterBeaconState);
  const employeesState = useSelector((state: any) => state.employeesState);

  const [search, setSearch] = useState<string>("");
  const [dataList, setDataList] = React.useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });
  const [selectedRows, setSelectedRows] = useState<number[] | null>(null);

  const handleReset = (data: number[]) => {
    if (tableRef.current) {
      tableRef.current.resetSelection(data);
    }
  };

  const formatDMY = (value: any) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Initial fetch
  React.useEffect(() => {
    console.log("beacon", beacon);
    if (!beacon) return;
    dispatch(
      getEmployees({ page: pagination.current, limit: pagination.pageSize, query: search } as any)
    );
    setSelectedRows(null);
  }, [beacon]);

  // Debounced search
  React.useEffect(() => {
    if (!beacon) return;
    const handler = setTimeout(() => {
      setPagination((p) => ({ ...p, current: 1 }));
      dispatch(getEmployees({ page: 1, limit: pagination.pageSize, query: search } as any));
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const handleAssign = async () => {
    if (!(beacon && selectedRows && selectedRows.length > 0)) return;
    const payload = {
      employee_ids: selectedRows,
      beacon_id: beacon.id,
    } as any;
    await dispatch(assignMasterBeacon(payload));
    handleReset([]);
    setSelectedRows(null);
    dispatch(
      getEmployees({ page: pagination.current, limit: pagination.pageSize, query: search } as any)
    );
  };

  // Process Employees list response (supports nested shape from employees/find-all)
  React.useEffect(() => {
    if (employeesState.loading === "fulfilled" && employeesState.action === "list") {
      const result = employeesState?.data?.data || null;
      const rows = Array.isArray(result?.data) ? result.data : [];
      const employees = rows.map((item: any) => {
        const details = item?.details || {};
        const org = details?.sap_department_designation_wings || {};
        const firstName = details?.firstname || details?.first_name || "";
        const lastName = details?.lsastname || details?.lastname || details?.last_name || "";
        const fullName = [firstName, lastName].filter(Boolean).join(" ") || details?.fullName || item?.name || "N/A";

        return {
          id: item?.id,
          empId: item?.empId || item?.emp_id || item?.usrid || "-",
          name: fullName,
          image: details?.image || null,
          email: details?.email || item?.email || item?.work_email || item?.office_email || "-",
          phone: details?.phone || item?.phone || item?.mobile || item?.contact || "-",
          dob: details?.birthdate || details?.date_of_birth || item?.dob || item?.birth_date || null,
          joiningDate:
            details?.date_of_joining || item?.date_of_joining || item?.joiningDate || item?.joining_date || item?.validityStart || null,
          wing: org?.wing_name || org?.wing || details?.wing || "-",
          department:
            org?.department_name ||
            org?.department ||
            details?.department_name ||
            details?.department ||
            item?.department ||
            "-",
          designation:
            org?.designation_name ||
            details?.designation ||
            details?.designationID ||
            item?.designation ||
            item?.designationID ||
            "-",
          reportingManager:
            details?.reportingManagerName || details?.reporting_manager_name || item?.manager_name || "-",
          role: item?.user_type || item?.emp_role || item?.hrms_role || "-",
          status: item?.status || "-",
          key: item?.id,
        };
      });

      setDataList(employees);
      setPagination({
        ...pagination,
        total: result?.total || employees.length || 0,
        current: result?.page || 1,
        pageSize: result?.limit || pagination.pageSize,
      });
    }
  }, [employeesState.loading, employeesState.action, employeesState.data]);

  // Removed auto-refresh on each single assign completion to avoid duplicate list reloads

  const handlePagination = (paginationArg: any) => {
    setPagination({
      current: paginationArg.page,
      pageSize: paginationArg.pageSize,
      total: paginationArg.total || 10,
    });
    dispatch(
      getEmployees({ page: paginationArg.page, limit: paginationArg.pageSize, query: search } as any)
    );
  };

  const handleOnRowSelection = (selectedRowKeys: number[]) => {
    setSelectedRows(selectedRowKeys);
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 90,
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index + pagination.current * pagination.pageSize - pagination.pageSize + 1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 300,
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
      render: (text: string, record: any) => {
        const initial = (record?.name || "?")?.toString()?.trim()?.charAt(0)?.toUpperCase() || "?";
        return (
          <div className="d-flex align-items-center" style={{ minWidth: 240 }}>
            {record?.image ? (
              <Avatar
                src={`data:image/jpeg;base64,${record.image}`}
                className="me-2"
                style={{ width: "40px", height: "40px" }}
              />
            ) : (
              <Avatar className="me-2" style={{ width: "40px", height: "40px" }}>{initial}</Avatar>
            )}
            <div className="text-primary text-truncate" style={{ maxWidth: 200 }}>{text}</div>
          </div>
        );
      },
    },
    { title: "Emp Id", dataIndex: "empId", width: 150, sorter: (a: any, b: any) => ("" + a.empId).localeCompare("" + b.empId) },
    { title: "Email", dataIndex: "email", width: 240, render: (val: string) => <div className="text-truncate" style={{ maxWidth: 220 }}>{val || "-"}</div> },
    { title: "Phone", dataIndex: "phone", width: 140, render: (v: string) => v || "-" },
    { title: "Wing", dataIndex: "wing", width: 180, render: (v: string) => <div className="text-truncate" style={{ maxWidth: 160 }}>{v || "-"}</div> },
    { title: "Department", dataIndex: "department", width: 200, render: (v: string) => <div className="text-truncate" style={{ maxWidth: 180 }}>{v || "-"}</div> },
    { title: "Designation", dataIndex: "designation", width: 200, render: (v: string) => <div className="text-truncate" style={{ maxWidth: 180 }}>{v || "-"}</div> },
  ];

  if (!beacon) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading beacon information...</p>
      </div>
    );
  }

  return ( 
    <div>
      <>
        <Toasts
          show={masterBeaconState.loading === "rejected"}
          message={masterBeaconState.error}
          title={"Failed"}
          type={"danger"}
        />
        <Toasts
          show={masterBeaconState.loading === "fulfilled" && masterBeaconState.action === "assign"}
          message="Master beacon assigned successfully"
          title={"Success"}
          type={"success"}
        />

        <div className="pt-2">
          {/* Beacon summary */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="card bg-light">
                <div className="card-body p-3">
                  <h6 className="card-title mb-2">
                    Current Beacon: {beacon.beaconName || `ID: ${beacon.id}`}
                  </h6>
                  <p className="card-text mb-1">
                    <strong>UUID:</strong> {beacon.uuid}
                    <br />
                    <strong>Location:</strong> {beacon.locationDescription || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Table provides its own search UI; removed duplicate search input */}

          <Table
            columns={columns}
            dataSource={dataList}
            Selection={true}
            paginationConfig={pagination}
            handlePagination={handlePagination}
            handleSearch={(q: string) => setSearch(q)}
            loading={employeesState.loading === "pending"}
            onRowSelection={handleOnRowSelection}
            ref={tableRef}
            scroll={{ y: 300, x: 1200 }}
          />
        </div>
        <div className="row align-items-center">
          <div className="col-md-12">
            <div className="datatable-paginate mt-4" />
            <div className="d-flex justify-content-center pb-3 pt-0">
              {selectedRows && selectedRows.length > 0 ? (
                <Link
                  to="#"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                  onClick={() => handleAssign()}
                >
                  Assign Beacon to {selectedRows.length} Employee
                  {selectedRows.length > 1 ? "s" : ""}
                </Link>
              ) : (
                <Link to="#" data-bs-dismiss="modal" className="btn btn-primary">
                  Select Employees to Assign
                </Link>
              )}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default AssignMasterBeacon;
